import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetJson } from '../helpers/fetchReact';
import { REST_API, GeoAreaTypes, STATE_STATUS } from '../constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { createSelector } from 'reselect';

const initialState = {
  roles: [],
  userTypes: [],
  accessRequestStatuses: [],
  permissions: [],
  observationTypes: [],
  avalancheTechnicians: [],
  hazardtechnicians: [],
  internalIngestionCandidateStatus: [],
  nonAvalancheGeographData: [],
  weatherstationStatuses: [],
  sensorTypes: [],
  weatherStationGeographicAreas: [],
  weatherStationObservationTypes: [],
  status: {
    roles: STATE_STATUS.IDLE,
    userTypes: STATE_STATUS.IDLE,
    accessRequestStatuses: STATE_STATUS.IDLE,
    permissions: STATE_STATUS.IDLE,
    observationTypes: STATE_STATUS.IDLE,
    avalancheTechnicians: STATE_STATUS.IDLE,
    hazardtechnicians: STATE_STATUS.IDLE,
    internalIngestionCandidateStatus: STATE_STATUS.IDLE,
    nonAvalancheGeographData: STATE_STATUS.IDLE,
    weatherstationStatuses: STATE_STATUS.IDLE,
    sensorTypes: STATE_STATUS.IDLE,
    weatherStationGeographicAreas: STATE_STATUS.IDLE,
    weatherStationObservationTypes: STATE_STATUS.IDLE,
  },
};

export const selectRoles = (state) => {
  return { options: state.dropdowns.roles, status: state.dropdowns.status.roles };
};
export const fetchRoles = createAsyncThunk('dropdowns/roles', async () => {
  const url = REST_API.DROPDOWNS + 'roles';
  return GetJson(url).then((response) => response.json());
});

export const selectUserTypes = (state) => {
  return { options: state.dropdowns.userTypes, status: state.dropdowns.status.userTypes };
};
export const fetchUserTypes = createAsyncThunk('dropdowns/userTypes', async () => {
  const url = REST_API.DROPDOWNS + 'usertypes';
  return GetJson(url).then((response) => response.json());
});

export const selectAccessRequestStatuses = (state) => {
  return { options: state.dropdowns.accessRequestStatuses, status: state.dropdowns.status.accessRequestStatuses };
};
export const fetchAccessRequestStatuses = createAsyncThunk('dropdowns/accessRequestStatuses', async () => {
  const url = REST_API.DROPDOWNS + 'accessRequestStatuses';
  return GetJson(url).then((response) => response.json());
});

export const selectPermissions = (state) => {
  return { options: state.dropdowns.permissions, status: state.dropdowns.status.permissions };
};
export const fetchPermissions = createAsyncThunk('dropdowns/permissions', async () => {
  const url = REST_API.DROPDOWNS + 'permissions';
  return GetJson(url).then((response) => response.json());
});

export const selectObservationTypes = (state) => {
  return { options: state.dropdowns.observationTypes, status: state.dropdowns.status.observationTypes };
};
export const fetchObservationTypes = createAsyncThunk('dropdowns/observationtypes', async () => {
  const url = REST_API.DROPDOWNS + 'observationtypes';
  return GetJson(url).then((response) => response.json());
});

export const selectAvalancheTechnicians = (state) => {
  return { options: state.dropdowns.avalancheTechnicians, status: state.dropdowns.status.avalancheTechnicians };
};
export const selectHazardTechnicians = (state) => {
  const ops = state.dropdowns.hazardTechnicians.map((opt) => {
    return {
      ...opt,
      isDisabled: !opt.isactive,
    };
  });
  return { options: ops, status: state.dropdowns.status.hazardTechnicians };
};
export const fetchAvalancheTechnicians = createAsyncThunk('dropdowns/avalanchetechnicians', async () => {
  const url = REST_API.DROPDOWNS + 'avalanchetechnicians';
  return GetJson(url).then((response) => response.json());
});
export const fetchHazardTechnicians = createAsyncThunk('dropdowns/hazardtechnicians', async () => {
  const url = REST_API.DROPDOWNS + 'hazardtechnicians';
  return GetJson(url).then((response) => response.json());
});
export const selectInternalIngestionCandidateStatus = (state) => {
  return {
    options: state.dropdowns.internalIngestionCandidateStatus,
    status: state.dropdowns.status.internalIngestionCandidateStatus,
  };
};
export const fetchInternalIngestionCandidateStatus = createAsyncThunk(
  'dropdowns/internalIngestionCandidateStatus',
  async () => {
    const url = REST_API.DROPDOWNS + 'internalingestioncandidatestatus';
    return GetJson(url).then((response) => response.json());
  },
);

export const selectNonAvalancheGeographData = (state) => {
  return {
    options: state.dropdowns.nonAvalancheGeographData,
    status: state.dropdowns.status.nonAvalancheGeographData,
  };
};
export const fetchNonAvalancheGeographData = createAsyncThunk('dropdowns/nonAvalancheGeographData', async () => {
  const url = REST_API.DROPDOWNS + 'nonavalanchegeographdata';
  return GetJson(url).then((response) => response.json());
});
export const selectContractAreas = (state) => {
  const areas = [...state.dropdowns.nonAvalancheGeographData];
  let ops = [];
  !isValueEmpty(areas)
    ? (ops = areas.filter((o) => {
        return o?.typename === GeoAreaTypes.CONTRACT;
      }))
    : [];

  return ops;
};
export const selectEetAreas = (state) => {
  const areas = [...state.dropdowns.nonAvalancheGeographData];
  let ops = [];
  !isValueEmpty(areas)
    ? (ops = areas.filter((o) => {
        return o?.typename === GeoAreaTypes.EET;
      }))
    : [];
  return ops;
};

export const selectSensorTypes = (state) => {
  return {
    options: state.dropdowns.sensorTypes,
    status: state.dropdowns.status.sensorTypes,
  };
};

export const selectSensorTypeByName = (name) => {
  return createSelector([selectSensorTypes], (allSensorTypes) => {
    return allSensorTypes.options?.find((sensor) => sensor.name === name);
  });
};

export const fetchSensorTypes = createAsyncThunk('dropdowns/sensorTypes', async () => {
  const url = REST_API.DROPDOWNS + 'sensortypes';
  return GetJson(url).then((response) => response.json());
});

export const selectWeatherStationGeographicAreas = (state) => {
  return {
    options: state.dropdowns.weatherStationGeographicAreas,
    status: state.dropdowns.status.weatherStationGeographicAreas,
  };
};
export const fetchWeatherStationGeographicAreas = createAsyncThunk(
  'dropdowns/weatherStationGeographicAreas',
  async () => {
    const url = REST_API.DROPDOWNS + 'weatherstationgeographicareas';
    return GetJson(url).then((response) => response.json());
  },
);

export const selectWeatherStationObservationTypes = (state) => {
  return {
    options: state.dropdowns.weatherStationObservationTypes,
    status: state.dropdowns.status.weatherStationObservationTypes,
  };
};
export const fetchWeatherStationObservationTypes = createAsyncThunk(
  'dropdowns/weatherstationobservationtypes',
  async () => {
    const url = REST_API.DROPDOWNS + 'weatherstationobservationtypes';
    return GetJson(url).then((response) => response.json());
  },
);

export const dropdownsSlice = createSlice({
  name: 'dropdowns',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchRoles.pending]: (state) => {
      state.status.roles = STATE_STATUS.LOADING;
    },
    [fetchRoles.fulfilled]: (state, action) => {
      state.roles = action.payload;
      state.status.roles = STATE_STATUS.SUCCESS;
    },
    [fetchRoles.rejected]: (state) => {
      state.status.roles = STATE_STATUS.FAILED;
    },
    [fetchUserTypes.pending]: (state) => {
      state.status.userTypes = STATE_STATUS.LOADING;
    },
    [fetchUserTypes.fulfilled]: (state, action) => {
      state.userTypes = action.payload;
      state.status.userTypes = STATE_STATUS.SUCCESS;
    },
    [fetchUserTypes.rejected]: (state) => {
      state.status.userTypes = STATE_STATUS.FAILED;
    },
    [fetchAccessRequestStatuses.pending]: (state) => {
      state.status.accessRequestStatuses = STATE_STATUS.LOADING;
    },
    [fetchAccessRequestStatuses.fulfilled]: (state, action) => {
      state.accessRequestStatuses = action.payload;
      state.status.accessRequestStatuses = STATE_STATUS.SUCCESS;
    },
    [fetchAccessRequestStatuses.rejected]: (state) => {
      state.status.accessRequestStatuses = STATE_STATUS.FAILED;
    },
    [fetchPermissions.pending]: (state) => {
      state.status.permissions = STATE_STATUS.LOADING;
    },
    [fetchPermissions.fulfilled]: (state, action) => {
      state.permissions = action.payload;
      state.status.permissions = STATE_STATUS.SUCCESS;
    },
    [fetchPermissions.rejected]: (state) => {
      state.status.permissions = STATE_STATUS.FAILED;
    },
    [fetchObservationTypes.pending]: (state) => {
      state.status.observationTypes = STATE_STATUS.LOADING;
    },
    [fetchObservationTypes.fulfilled]: (state, action) => {
      state.observationTypes = action.payload;
      state.status.observationTypes = STATE_STATUS.SUCCESS;
    },
    [fetchObservationTypes.rejected]: (state) => {
      state.status.observationTypes = STATE_STATUS.FAILED;
    },
    [fetchAvalancheTechnicians.pending]: (state) => {
      state.status.avalancheTechnicians = STATE_STATUS.LOADING;
    },
    [fetchAvalancheTechnicians.fulfilled]: (state, action) => {
      state.avalancheTechnicians = action.payload;
      state.status.avalancheTechnicians = STATE_STATUS.SUCCESS;
    },
    [fetchAvalancheTechnicians.rejected]: (state) => {
      state.status.avalancheTechnicians = STATE_STATUS.FAILED;
    },
    [fetchHazardTechnicians.fulfilled]: (state, action) => {
      state.hazardTechnicians = action.payload;
      state.status.hazardTechnicians = STATE_STATUS.SUCCESS;
    },
    [fetchHazardTechnicians.rejected]: (state) => {
      state.status.hazardTechnicians = STATE_STATUS.FAILED;
    },
    [fetchInternalIngestionCandidateStatus.pending]: (state) => {
      state.status.internalIngestionCandidateStatus = STATE_STATUS.LOADING;
    },
    [fetchInternalIngestionCandidateStatus.fulfilled]: (state, action) => {
      state.internalIngestionCandidateStatus = action.payload;
      state.status.internalIngestionCandidateStatus = STATE_STATUS.SUCCESS;
    },
    [fetchInternalIngestionCandidateStatus.rejected]: (state) => {
      state.status.internalIngestionCandidateStatus = STATE_STATUS.FAILED;
    },
    [fetchNonAvalancheGeographData.pending]: (state) => {
      state.status.nonAvalancheGeographData = STATE_STATUS.LOADING;
    },
    [fetchNonAvalancheGeographData.fulfilled]: (state, action) => {
      state.nonAvalancheGeographData = action.payload;
      state.status.nonAvalancheGeographData = STATE_STATUS.SUCCESS;
    },
    [fetchNonAvalancheGeographData.rejected]: (state) => {
      state.status.nonAvalancheGeographData = STATE_STATUS.FAILED;
    },
    [fetchSensorTypes.pending]: (state) => {
      state.status.sensorTypes = STATE_STATUS.LOADING;
    },
    [fetchSensorTypes.fulfilled]: (state, action) => {
      state.sensorTypes = action.payload;
      state.status.sensorTypes = STATE_STATUS.SUCCESS;
    },
    [fetchSensorTypes.rejected]: (state) => {
      state.status.sensorTypes = STATE_STATUS.FAILED;
    },
    [fetchWeatherStationGeographicAreas.pending]: (state) => {
      state.status.weatherStationGeographicAreas = STATE_STATUS.LOADING;
    },
    [fetchWeatherStationGeographicAreas.fulfilled]: (state, action) => {
      state.weatherStationGeographicAreas = action.payload;
      state.status.weatherStationGeographicAreas = STATE_STATUS.SUCCESS;
    },
    [fetchWeatherStationGeographicAreas.rejected]: (state) => {
      state.status.weatherStationGeographicAreas = STATE_STATUS.FAILED;
    },
    [fetchWeatherStationObservationTypes.pending]: (state) => {
      state.status.weatherStationObservationTypes = STATE_STATUS.LOADING;
    },
    [fetchWeatherStationObservationTypes.fulfilled]: (state, action) => {
      state.weatherStationObservationTypes = action.payload;
      state.status.weatherStationObservationTypes = STATE_STATUS.SUCCESS;
    },
    [fetchWeatherStationObservationTypes.rejected]: (state) => {
      state.status.weatherStationObservationTypes = STATE_STATUS.FAILED;
    },
  },
});
